import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "searchInput",
    "loader",
    "noData",
    "tableContainer",
    "tableHeader",
    "tableBody",
    "pagination"
  ]
  static values = {
    surveyId: String
  }

  connect() {
    this.currentPage = 1
    this.perPage = 10
    this.allAnswers = []
    this.filteredAnswers = []
    this.questions = []
    this.expandedRows = new Set()

    this.loadData()
  }

  async loadData() {
    try {
      const response = await fetch(`/api/v1/surveys/${this.surveyIdValue}/answers.json`)
      const data = await response.json()

      this.allAnswers = data.answers
      this.filteredAnswers = [...this.allAnswers]
      this.questions = data.questions

      if (this.allAnswers.length === 0) {
        this.showNoData()
      } else {
        this.renderTable()
      }
    } catch (error) {
      console.error('Error loading survey answers:', error)
      this.showNoData()
    }
  }

  showNoData() {
    this.loaderTarget.classList.add('d-none')
    this.noDataTarget.classList.remove('d-none')
    this.tableContainerTarget.classList.add('d-none')
  }

  renderTable() {
    this.loaderTarget.classList.add('d-none')
    this.noDataTarget.classList.add('d-none')
    this.tableContainerTarget.classList.remove('d-none')

    this.renderHeaders()
    this.renderRows()
    this.renderPagination()
  }

  renderHeaders() {
    const headers = ['<th>' + (window.t ? window.t('course.license.user_name') : 'User Name') + '</th>']

    this.questions.forEach(question => {
      headers.push(`<th>${this.escapeHtml(question.name)}</th>`)
    })

    headers.push('<th></th>') // Actions column

    this.tableHeaderTarget.innerHTML = headers.join('')
  }

  renderRows() {
    const start = (this.currentPage - 1) * this.perPage
    const end = start + this.perPage
    const pageAnswers = this.filteredAnswers.slice(start, end)

    const rows = []

    pageAnswers.forEach((answer, index) => {
      const rowId = `answer-row-${start + index}`
      const isExpanded = this.expandedRows.has(rowId)

      // Main row
      let row = '<tr>'
      row += `<td><strong>${this.escapeHtml(answer.user_name)}</strong></td>`

      this.questions.forEach(question => {
        const answerText = answer[`q${question.id}`] || ''
        const truncated = this.truncateText(answerText, 50)
        row += `<td>${this.escapeHtml(truncated)}</td>`
      })

      row += `<td>
        <button
          class="btn btn-sm btn-link"
          data-action="click->survey-report#toggleRow"
          data-row-id="${rowId}"
        >
          <i class="fas fa-chevron-${isExpanded ? 'up' : 'down'}"></i>
        </button>
      </td>`
      row += '</tr>'

      rows.push(row)

      // Expanded row
      if (isExpanded) {
        let expandedRow = `<tr id="${rowId}" class="table-active">`
        expandedRow += '<td colspan="' + (this.questions.length + 2) + '">'
        expandedRow += '<div class="p-3">'

        this.questions.forEach(question => {
          const answerText = answer[`q${question.id}`] || ''
          expandedRow += `
            <div class="mb-3">
              <strong>${this.escapeHtml(question.name)}:</strong>
              <div class="mt-1">${this.escapeHtml(answerText)}</div>
            </div>
          `
        })

        expandedRow += '</div></td></tr>'
        rows.push(expandedRow)
      }
    })

    this.tableBodyTarget.innerHTML = rows.join('')
  }

  renderPagination() {
    const totalPages = Math.ceil(this.filteredAnswers.length / this.perPage)

    if (totalPages <= 1) {
      this.paginationTarget.innerHTML = ''
      return
    }

    const pages = []

    // Previous button
    pages.push(`
      <li class="page-item ${this.currentPage === 1 ? 'disabled' : ''}">
        <button
          class="page-link"
          data-action="click->survey-report#previousPage"
          ${this.currentPage === 1 ? 'disabled' : ''}
        >
          &laquo;
        </button>
      </li>
    `)

    // Page numbers
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= this.currentPage - 2 && i <= this.currentPage + 2)
      ) {
        pages.push(`
          <li class="page-item ${i === this.currentPage ? 'active' : ''}">
            <button
              class="page-link"
              data-action="click->survey-report#goToPage"
              data-page="${i}"
            >
              ${i}
            </button>
          </li>
        `)
      } else if (
        i === this.currentPage - 3 ||
        i === this.currentPage + 3
      ) {
        pages.push('<li class="page-item disabled"><span class="page-link">...</span></li>')
      }
    }

    // Next button
    pages.push(`
      <li class="page-item ${this.currentPage === totalPages ? 'disabled' : ''}">
        <button
          class="page-link"
          data-action="click->survey-report#nextPage"
          ${this.currentPage === totalPages ? 'disabled' : ''}
        >
          &raquo;
        </button>
      </li>
    `)

    this.paginationTarget.innerHTML = pages.join('')
  }

  filterTable() {
    const searchText = this.searchInputTarget.value.toLowerCase()

    if (searchText === '') {
      this.filteredAnswers = [...this.allAnswers]
    } else {
      this.filteredAnswers = this.allAnswers.filter(answer =>
        answer.user_name && answer.user_name.toLowerCase().includes(searchText)
      )
    }

    this.currentPage = 1
    this.renderRows()
    this.renderPagination()
  }

  clearFilter() {
    this.searchInputTarget.value = ''
    this.filterTable()
  }

  toggleRow(event) {
    const rowId = event.currentTarget.dataset.rowId

    if (this.expandedRows.has(rowId)) {
      this.expandedRows.delete(rowId)
    } else {
      this.expandedRows.add(rowId)
    }

    this.renderRows()
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--
      this.renderRows()
      this.renderPagination()
    }
  }

  nextPage() {
    const totalPages = Math.ceil(this.filteredAnswers.length / this.perPage)
    if (this.currentPage < totalPages) {
      this.currentPage++
      this.renderRows()
      this.renderPagination()
    }
  }

  goToPage(event) {
    const page = parseInt(event.currentTarget.dataset.page)
    this.currentPage = page
    this.renderRows()
    this.renderPagination()
  }

  // Helper methods
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  truncateText(text, length) {
    if (!text || text.length <= length) return text
    return text.substring(0, length) + '...'
  }
}
